import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Coins,
  Rocket,
  Heart,
  Star,
  GitBranch,
  Plus,
  Check,
  Lock,
  Sparkles,
  Zap,
} from "lucide-react";
import { playClick, playWin } from "../game/sound";
import { useAuth } from "../contexts/AuthContext";
import { fetchTalentsCatalog, spendTalent } from "../game/authService";

const BRANCHES = [
  { id: "economy",     label: "Economy",     Icon: Coins,    color: "#ffd06b",
    blurb: "More coins. More crates. More rewards." },
  { id: "ufo",         label: "UFO",         Icon: Rocket,   color: "#84e6ff",
    blurb: "Permanent UFO and beam tuning." },
  { id: "survival",    label: "Survival",    Icon: Heart,    color: "#ff7ab1",
    blurb: "Stay alive longer. Take less punishment." },
  { id: "progression", label: "Progression", Icon: Sparkles, color: "#c2ff5d",
    blurb: "Level up faster. Earn more XP." },
];

function TalentCard({ talent, currentLevel, canBuy, busy, onBuy, accent }) {
  const max = talent.maxLevel || 5;
  const isMax = currentLevel >= max;
  const future = !!talent.future;
  return (
    <div
      className={`tal-card ${isMax ? "maxed" : ""} ${future ? "future" : ""}`}
      data-testid={`tal-card-${talent.id}`}
    >
      <div className="tal-card-head">
        <div className="tal-card-title" style={{ color: accent }}>
          <Zap size={14} /> {talent.name}
        </div>
        <div className="tal-card-lvl" data-testid={`tal-lvl-${talent.id}`}>
          {currentLevel} / {max}
        </div>
      </div>
      <div className="tal-card-desc">{talent.desc}</div>
      <div className="tal-card-pips">
        {Array.from({ length: max }).map((_, i) => (
          <span
            key={i}
            className={`tal-pip ${i < currentLevel ? "filled" : ""}`}
            style={i < currentLevel ? { background: accent } : undefined}
          />
        ))}
      </div>
      <div className="tal-card-footer">
        {future && (
          <span className="tal-future-tag">
            <Lock size={11} /> Effect coming soon
          </span>
        )}
        {isMax ? (
          <div className="tal-maxed-tag">
            <Check size={14} /> Maxed
          </div>
        ) : (
          <button
            type="button"
            className="zc-btn tal-buy"
            disabled={!canBuy || busy}
            onClick={() => onBuy(talent.id)}
            data-testid={`tal-buy-${talent.id}`}
          >
            <Plus size={14} /> {busy ? "..." : "Spend 1 Point"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TalentsScreen({ onBack }) {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchTalentsCatalog();
        if (cancelled) return;
        if (!list || list.length === 0) {
          setLoadErr("Could not load talents — please refresh.");
        }
        setCatalog(list || []);
      } catch (_e) {
        if (!cancelled) setLoadErr("Could not load talents — please refresh.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tree = user?.talent_tree || {};
  const points = user?.talent_points || 0;
  const grouped = useMemo(() => {
    const out = {};
    BRANCHES.forEach((b) => (out[b.id] = []));
    catalog.forEach((t) => {
      if (out[t.branch]) out[t.branch].push(t);
    });
    return out;
  }, [catalog]);

  const buy = async (talentId) => {
    if (points <= 0) return;
    setErr("");
    setBusy(talentId);
    playClick();
    try {
      await spendTalent(talentId);
      await refresh();
      playWin();
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not spend point");
    } finally {
      setBusy(null);
    }
  };

  if (!user) {
    return (
      <div className="screen placeholder-screen" data-testid="talents-screen">
        <button
          type="button"
          className="zc-btn icon-back"
          onClick={() => { playClick(); onBack(); }}
        >
          <ArrowLeft size={18} /> Back
        </button>
        <div className="placeholder-wrap">
          <h1 className="placeholder-title">Talent Tree</h1>
          <p className="placeholder-blurb">
            Sign in with Google to spend Talent Points on permanent passives.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen talents-screen" data-testid="talents-screen">
      <button
        type="button"
        className="zc-btn icon-back"
        onClick={() => { playClick(); onBack(); }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <div className="tal-wrap">
        <h1 className="tal-title">
          <GitBranch size={26} /> Talent Tree
        </h1>
        <p className="tal-tag">
          Spend Talent Points on permanent passives that carry across every run.
          Each Account Level you earn grants 1 Talent Point.
        </p>

        <div className={`tal-header ${flash ? "flash" : ""}`} data-testid="tal-header">
          <div className="tal-header-stat">
            <span className="tal-header-label">Account Level</span>
            <span className="tal-header-value" data-testid="tal-account-level">
              {user.level || 1}
            </span>
          </div>
          <div className="tal-header-stat tal-header-points">
            <span className="tal-header-label">Talent Points</span>
            <span
              className="tal-header-value tal-points-pill"
              data-testid="tal-points-available"
            >
              <Star size={14} /> {points}
            </span>
          </div>
          <div className="tal-header-stat">
            <span className="tal-header-label">Total XP</span>
            <span className="tal-header-value" data-testid="tal-total-xp">
              {(user.xp || 0).toLocaleString()}
            </span>
          </div>
        </div>

        {points === 0 && (
          <div className="tal-empty-hint" data-testid="tal-empty-hint">
            <Lock size={14} /> No talent points yet — finish runs to earn XP and level up.
          </div>
        )}

        {BRANCHES.map((b) => {
          const items = grouped[b.id] || [];
          if (!items.length) return null;
          const Icon = b.Icon;
          return (
            <section
              key={b.id}
              className="tal-branch"
              data-testid={`tal-branch-${b.id}`}
            >
              <div className="tal-branch-head" style={{ color: b.color }}>
                <Icon size={20} />
                <h2>{b.label}</h2>
                <span className="tal-branch-blurb">{b.blurb}</span>
              </div>
              <div className="tal-branch-grid">
                {items.map((t) => (
                  <TalentCard
                    key={t.id}
                    talent={t}
                    currentLevel={tree[t.id] || 0}
                    canBuy={points > 0}
                    busy={busy === t.id}
                    onBuy={buy}
                    accent={b.color}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {err && <div className="vip-err" data-testid="tal-err">{err}</div>}
        {loadErr && (
          <div className="vip-err" data-testid="tal-load-err">{loadErr}</div>
        )}
      </div>
    </div>
  );
}
